/**
 * getUserLocation — smart geolocation with error-code-aware retry.
 *
 * Behavior:
 *   1. First attempt: high-accuracy GPS, 10s timeout, 60s cache-max
 *   2. If timeout → retry with LOW accuracy (Wi-Fi/cell), 15s timeout, 5min cache-max
 *   3. If either resolves → return coords
 *   4. If permission denied at any point → reject immediately with reason:"denied"
 *      unless we're inside a cross-origin iframe whose Permissions-Policy strips
 *      the "geolocation" feature (Emergent Preview wrapper, GitHub Pages
 *      preview, etc.) — in which case reject with reason:"iframe_blocked" so
 *      the recovery UI can offer an "Open in Safari" escape hatch.
 *   5. If unavailable → reject with reason:"unavailable"
 *   6. If both attempts time out → reject with reason:"timeout"
 *
 * Returns Promise<{ coords, reason }>.
 * Reject value:
 *   { reason: "denied" | "unavailable" | "timeout" | "unsupported" | "iframe_blocked", error }
 */

/** True when the page is loaded inside an iframe (any parent). */
export function isFramed() {
  try { return window.self !== window.top; }
  catch { return true; /* cross-origin parent throws on `top` access — that IS a frame */ }
}

/**
 * True when the current document's Permissions-Policy explicitly disallows
 * geolocation (usually because the parent iframe didn't grant it via `allow`).
 * Safe to call unconditionally — returns false when the API is unavailable.
 */
function policyBlocksGeolocation() {
  try {
    // Chromium: document.featurePolicy.allowsFeature
    if (document.featurePolicy?.allowsFeature) {
      return !document.featurePolicy.allowsFeature("geolocation");
    }
    // Standard (newer): document.permissionsPolicy
    if (document.permissionsPolicy?.allowsFeature) {
      return !document.permissionsPolicy.allowsFeature("geolocation");
    }
  } catch { /* API missing on this browser — fall through */ }
  return false;
}

function classifyDenied() {
  // If a Permissions-Policy explicitly blocks geolocation OR we're framed, the
  // "denied" wasn't the user's device setting — it's the frame policy.
  if (policyBlocksGeolocation()) return "iframe_blocked";
  if (isFramed()) return "iframe_blocked";
  return "denied";
}

export function getUserLocation() {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject({ reason: "unsupported" });
      return;
    }
    const done = (coords) => resolve({ coords, reason: "ok" });
    const fail = (r, e) => reject({ reason: r, error: e });

    // 1st attempt — high accuracy
    navigator.geolocation.getCurrentPosition(
      (pos) => done(pos.coords),
      (err) => {
        // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        if (err.code === 1) return fail(classifyDenied(), err);
        if (err.code === 2) {
          // Try a second time with lower accuracy — sometimes recovers on iOS
          navigator.geolocation.getCurrentPosition(
            (pos) => done(pos.coords),
            (err2) => fail(
              err2.code === 1 ? classifyDenied()
                : (err2.code === 3 ? "timeout" : "unavailable"),
              err2
            ),
            { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 }
          );
          return;
        }
        // TIMEOUT — retry with lower accuracy and a longer window
        navigator.geolocation.getCurrentPosition(
          (pos) => done(pos.coords),
          (err2) => fail(
            err2.code === 1 ? classifyDenied()
              : (err2.code === 2 ? "unavailable" : "timeout"),
            err2
          ),
          { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 }
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  });
}
